import { useState, useEffect, useRef } from 'react';
import { api, Conversation, CoachMessage } from '../../lib/api';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  MapPinIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  InboxIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const phaseNames: Record<number, string> = {
  1: 'Business Profile', 2: 'Business Intel', 3: 'Brand Guide', 4: 'ICP',
  5: 'Assets', 6: 'Website', 7: 'Blog', 8: 'Social', 9: 'Messaging', 10: 'Activation',
};

export default function AdminCoachInboxPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadConversations = async () => {
    try {
      const data = await api.getConversations();
      setConversations(data.conversations);
      if (data.conversations.length > 0 && !selectedId) {
        if (window.innerWidth >= 1024) {
          setSelectedId(data.conversations[0].client_id);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadArchivedConversations = async () => {
    try {
      const data = await api.getArchivedConversations();
      setArchivedConversations(data.conversations);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showArchived) loadArchivedConversations();
  }, [showArchived]);

  useEffect(() => {
    if (!selectedId) return;
    const loadConvo = async () => {
      try {
        const data = await api.getConversation(selectedId);
        setMessages(data.messages);
        setClientInfo(data.client);
        await api.markConversationRead(selectedId);
        setConversations(prev => prev.map(c =>
          c.client_id === selectedId ? { ...c, unread_count: 0 } : c
        ));
      } catch (e) { console.error(e); }
    };
    loadConvo();
    const interval = setInterval(loadConvo, 10000);
    return () => clearInterval(interval);
  }, [selectedId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || !selectedId || sending) return;
    setSending(true);
    try {
      const result = await api.replyToConversation(selectedId, reply.trim());
      setMessages(prev => [...prev, result.message]);
      setReply('');
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleArchive = async (clientId: string) => {
    try {
      await api.archiveConversation(clientId);
      setConversations(prev => prev.filter(c => c.client_id !== clientId));
      if (selectedId === clientId) {
        setSelectedId(conversations.find(c => c.client_id !== clientId)?.client_id || null);
        setMessages([]);
        setClientInfo(null);
      }
      if (showArchived) loadArchivedConversations();
    } catch (e) { console.error(e); }
  };

  const handleUnarchive = async (clientId: string) => {
    try {
      await api.unarchiveConversation(clientId);
      setArchivedConversations(prev => prev.filter(c => c.client_id !== clientId));
      loadConversations();
    } catch (e) { console.error(e); }
  };

  const handleBackToList = () => {
    setSelectedId(null);
    setMessages([]);
    setClientInfo(null);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString();
  };

  const displayConversations = showArchived ? archivedConversations : conversations;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  const ConversationList = () => (
    <>
      <div className="flex border-b border-gray-200">
        <button onClick={() => { setShowArchived(false); setSelectedId(null); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            !showArchived ? 'text-copper-600 border-b-2 border-copper-500 bg-copper-50/50' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <InboxIcon className="h-4 w-4" />
          Inbox ({conversations.length})
        </button>
        <button onClick={() => { setShowArchived(true); setSelectedId(null); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            showArchived ? 'text-copper-600 border-b-2 border-copper-500 bg-copper-50/50' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <ArchiveBoxIcon className="h-4 w-4" />
          Archived
        </button>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {displayConversations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <ChatBubbleLeftRightIcon className="h-10 w-10 mx-auto mb-2" />
            <p className="text-sm">{showArchived ? 'No archived conversations' : 'No conversations yet'}</p>
          </div>
        ) : displayConversations.map(c => (
          <div key={c.client_id} className={`flex items-center group ${
            selectedId === c.client_id ? 'bg-copper-50 border-l-2 border-copper-500' : 'hover:bg-gray-50'
          }`}>
            <button onClick={() => setSelectedId(c.client_id)}
              className="flex-1 text-left p-3 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-charcoal-800 truncate">{c.business_name}</span>
                {c.unread_count > 0 && (
                  <span className="bg-copper-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0">
                    {c.unread_count}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-1">Phase {c.current_phase} — {phaseNames[c.current_phase]}</p>
              {c.last_message && (
                <p className="text-xs text-gray-500 truncate">
                  {c.last_message.sender_type === 'coach' ? 'You: ' : ''}{c.last_message.message}
                </p>
              )}
            </button>
            <button onClick={() => showArchived ? handleUnarchive(c.client_id) : handleArchive(c.client_id)}
              className="p-2 mr-2 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
              title={showArchived ? 'Unarchive' : 'Archive'}>
              {showArchived ? <ArchiveBoxXMarkIcon className="h-4 w-4" /> : <ArchiveBoxIcon className="h-4 w-4" />}
            </button>
          </div>
        ))}
      </div>
    </>
  );

  const ChatArea = () => (
    <>
      {clientInfo && (
        <div className="p-3 sm:p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-charcoal-800 truncate">{clientInfo.business_name}</h3>
              <p className="text-xs text-gray-500 truncate">{clientInfo.email} &middot; Phase {clientInfo.current_phase}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                clientInfo.status === 'onboarding' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
              }`}>{clientInfo.status}</span>
              {!showArchived && (
                <button onClick={() => handleArchive(selectedId!)}
                  className="hidden sm:flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Archive conversation">
                  <ArchiveBoxIcon className="h-3.5 w-3.5" /> Archive
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender_type === 'coach' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 ${
              m.sender_type === 'coach'
                ? 'bg-teal-600 text-white rounded-br-md'
                : 'bg-gray-100 text-charcoal-800 rounded-bl-md'
            }`}>
              {m.page_url && m.sender_type === 'client' && (
                <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                  <MapPinIcon className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{m.page_url}</span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
              <p className={`text-xs mt-1 ${m.sender_type === 'coach' ? 'text-teal-200' : 'text-gray-400'}`}>
                {m.created_at ? formatTime(m.created_at) : ''}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {!showArchived && (
        <div className="p-3 border-t border-gray-200">
          <div className="flex gap-2">
            <input type="text" value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type your reply..."
              className="flex-1 min-w-0 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
            <button onClick={handleSend}
              disabled={sending || !reply.trim()}
              className="px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0">
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-charcoal-800">Coach Inbox</h1>

      {/* Desktop: side by side */}
      <div className="hidden lg:flex gap-4 h-[calc(100vh-180px)]">
        <div className="w-80 bg-white rounded-xl border border-gray-200 flex flex-col flex-shrink-0 overflow-hidden">
          <ConversationList />
        </div>
        <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden min-w-0">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400"><p>Select a conversation</p></div>
          ) : <ChatArea />}
        </div>
      </div>

      {/* Mobile: stacked */}
      <div className="lg:hidden">
        {!selectedId ? (
          <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden min-h-[60vh]">
            <ConversationList />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden h-[calc(100vh-180px)]">
            <button onClick={handleBackToList}
              className="flex items-center gap-2 px-4 py-2 text-sm text-copper-600 hover:text-copper-700 border-b border-gray-100 flex-shrink-0">
              <ArrowLeftIcon className="h-4 w-4" /> Back to conversations
            </button>
            <ChatArea />
          </div>
        )}
      </div>
    </div>
  );
}
